"use client";

import { useState, useEffect } from "react";
import { useStore } from "src/store/store";
import { debounce } from "src/utils/debounce";
import { useTranslation } from "react-i18next";
import { apiFetch } from "src/utils/apiFetch";
import AutoComplete, { Option } from "./AutoComplete";

export default function SearchInput({ searchType }: { searchType: string }) {
  const [isLoading, setLoading] = useState(false);
  const [isDisabled, setDisabled] = useState(false);
  const [value, setValue] = useState<Option>();
  const setSearchOptions = useStore((state) => state.setSearchOptions);
  const { t } = useTranslation();

  // Debounced version of fetchSuggestions
  const debouncedFetchSuggestions = debounce(async (title: string) => {
    setLoading(true);
    try {
      const endpoint =
        searchType === "catalogue"
          ? `/search-suggestions/products?title=${encodeURIComponent(title)}`
          : `/users/search/suggestions?name=${encodeURIComponent(title)}`;

      const res = await apiFetch(endpoint, "GET");
      const items = res?.data;

      const options = items?.map((item: any) => ({
        value: item?.id,
        label:
          searchType === "catalogue"
            ? item?.title_en
            : item?.first_name + " " + item?.last_name,
        img:
          searchType === "catalogue"
            ? item?.prodImages[0]?.image_url
            : item?.profile_img_url,
      }));
      setSearchOptions(options);
    } catch (error) {
      console.error("Error fetching suggestions:", error);
    } finally {
      setLoading(false);
    }
  }, 2000); // 300ms delay

  useEffect(() => {
    setSearchOptions([]);
    debouncedFetchSuggestions(""); // Initial fetch (if needed)

    return () => {
      // Cleanup the debounced function
      debouncedFetchSuggestions.cancel(); // Assuming debounce utility supports cancel
    };
  }, [searchType, setSearchOptions]);

  const searchOptions = useStore((state) => state.searchOptions);

  return (
    <AutoComplete
      options={searchOptions}
      emptyMessage="No results."
      placeholder={`${t("search_for_items")} ...`}
      isLoading={isLoading}
      onValueChange={setValue}
      value={value}
      disabled={isDisabled}
      searchType={searchType}
      onInputChange={debouncedFetchSuggestions} // Use the debounced function here
    />
  );
}
